// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define.
 *
 * The audience is someone with a dashcam card and an insurance form, not a
 * machine-learning engineer. This tool asks them to accept a ranking produced
 * by a neural network, which is a lot to ask, so every term the interface uses
 * is defined in place, in a sentence — including the ones that explain why the
 * tool refuses to tell them something.
 */

export const GLOSSARY: Record<string, [string, string]> = {
  keyframe: [
    'Keyframe',
    'A frame stored whole, rather than as the difference from the one before it. Recorders write one every second or two, and they can be decoded on their own — so sampling keyframes is far cheaper than decoding everything, and cuepoint can list them without decoding anything at all.',
  ],
  embedding: [
    'Embedding',
    'A list of 512 numbers summarising what is in a picture. The model that produces them was trained so that pictures and descriptions of the same thing land near each other, which is what lets a typed sentence be compared against a frame at all.',
  ],
  cosine: [
    'Similarity score',
    'How close a frame’s numbers are to your query’s numbers, between −1 and 1. It has no absolute meaning: 0.17 was an excellent match for one query in our own measurements and no better than average for another. That is why cuepoint ranks results instead of setting a pass mark.',
  ],
  baseline: [
    'Baseline',
    'The score of a typical frame of this video against this query — the middle of the distribution. It is the only honest thing to compare a result against, because it is measured from your footage rather than assumed. A top result barely above the baseline means the tool found nothing in particular.',
  ],
  nms: [
    'Merging nearby results',
    'One event usually spans many frames, and without this you would get eleven results for the same ten seconds. cuepoint takes the best frame, claims the footage around it, suppresses everything within the merge gap, and repeats — so each result is a different moment.',
  ],
  median: [
    'Smoothing',
    'A single frame can score high for the wrong reason: a headlight flare, a compression artefact, one blurred frame. Taking the middle value of each frame and its neighbours discards those outright instead of spreading them around.',
  ],
  scene: [
    'Scenes, not actions',
    'The model looks at each frame on its own and has no sense of time, so it can find things and places and conditions — a red car, a bicycle, wet road at night — but not movements. “A car turning left” collapses to “a car”. Describe what you would see in a single freeze-frame.',
  ],
  webgpu: [
    'WebGPU',
    'The browser’s access to your graphics card. Where it is available the scan is many times faster. Where it is not, everything falls back to WebAssembly, which a static site like this one can only run on a single processor core — which is the whole reason for the wait.',
  ],
  selfcheck: [
    'The self-check',
    'Before anything is scanned, cuepoint embeds one fixed image and one fixed sentence and compares the answers against values recorded in its own source. A miscompiled or badly quantised model does not crash — it returns confident nonsense — and this is the only way to catch that. We measured one build of this very model that agreed with the correct answer on zero of eight queries.',
  ],
};

let tip: HTMLElement | null = null;

function hide() {
  tip?.remove();
  tip = null;
}

function show(anchor: HTMLElement, key: string) {
  hide();
  const entry = GLOSSARY[key];
  if (!entry) return;

  const el = document.createElement('div');
  el.id = 'glossary-tip';
  el.setAttribute('role', 'tooltip');
  const strong = document.createElement('strong');
  strong.textContent = entry[0];
  el.append(strong, document.createTextNode(entry[1]));
  document.body.append(el);

  const rect = anchor.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  const margin = 8;
  let left = rect.left;
  if (left + box.width > window.innerWidth - margin) left = window.innerWidth - box.width - margin;
  if (left < margin) left = margin;
  let top = rect.bottom + 6;
  if (top + box.height > window.innerHeight - margin) top = Math.max(margin, rect.top - box.height - 6);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  tip = el;
}

/** Wraps a term for use inside an innerHTML string. */
export function term(key: string, label?: string): string {
  const text = label ?? key;
  if (!GLOSSARY[key]) return text;
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${text}</span>`;
}

export function initGlossary() {
  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      if (tip && !tip.contains(event.target as Node)) hide();
      return;
    }
    event.preventDefault();
    const key = target.dataset.term;
    if (key) show(target, key);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && tip) {
      // Only swallow the key when a tooltip is actually up, so Escape still
      // reaches whatever overlay is behind it.
      event.stopPropagation();
      hide();
      return;
    }
    const target = event.target as HTMLElement | null;
    if ((event.key === 'Enter' || event.key === ' ') && target?.classList.contains('glossary-link')) {
      event.preventDefault();
      const key = target.dataset.term;
      if (key) show(target, key);
    }
  });

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
